<template>
  <el-form
    class="operations-search-form"
    size="small"
    label-position="top"
  >
    <el-row
      :gutter="20"
      type="flex"
    >
      <el-col :span="8">
        <el-form-item>
          <span slot="label">{{ $t('operations.search.description') }}</span>
          <el-autocomplete
            v-model="formData.description"
            value-key="label"
            clearable
            :fetch-suggestions="
              (text, cb) => {
                searchAnalyses({ text, cb, field: 'description' });
              }
            "
            :trigger-on-focus="false"
            @select="searchOperations"
            @change="emptySelectedAnalysis"
          >
            <template slot-scope="{ item }">
              <AutocompleteAnalysis :item="item" />
            </template>
          </el-autocomplete>
        </el-form-item>
      </el-col>

      <el-col :span="6">
        <el-form-item>
          <span slot="label">{{ $t('operations.search.code') }}</span>
          <el-autocomplete
            v-model="formData.code"
            value-key="label"
            clearable
            :fetch-suggestions="
              (text, cb) => {
                searchAnalyses({ text, cb, field: 'code' });
              }
            "
            :trigger-on-focus="false"
            @select="searchOperations"
            @change="emptySelectedAnalysis"
          >
            <template slot-scope="{ item }">
              <AutocompleteAnalysis
                :item="item"
                :showIcon="false"
              />
            </template>
          </el-autocomplete>
        </el-form-item>
      </el-col>

      <el-col :span="4">
        <el-form-item>
          <span slot="label">{{ $t('operations.search.analysisNumber') }}</span>
          <el-autocomplete
            v-model="formData.analysisNumber"
            value-key="label"
            clearable
            :fetch-suggestions="
              (text, cb) => {
                searchAnalysesById({ text, cb });
              }
            "
            :trigger-on-focus="false"
            @select="searchOperations"
            @change="emptySelectedAnalysis"
          >
            <template slot-scope="{ item }">
              <AutocompleteAnalysis
                :item="item"
                :showIcon="false"
                showNumber
              />
            </template>
          </el-autocomplete>
        </el-form-item>
      </el-col>
    </el-row>
  </el-form>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import AutocompleteAnalysis from '@/components/AutocompleteAnalysis.vue';

  import { analysesApi } from '@/api';

  const initialFormData = {
    description: '',
    code: '',
    analysisId: null,
    analysisNumber: '',
    instrument: '',
    method: '',
    methodCode: '',
    showTemperature: false,
    showSymbol: false,
    showDilution: false,
    defaultDilution: null,
  };

  const mapAnalysisSuggestion = (a) => ({
    value: a.id,
    code: a.code,
    description: a.description,
    method: a.method,
    methodCode: a.methodCode,
    accreditedTest: a.accreditedTest,
    instrument: a.brand,
    showTemperature: a.showTemperature ?? false,
    showSymbol: a.showSymbol ?? false,
    showDilution: a.showDilution ?? false,
    defaultDilution: a.defaultDilution ?? null,
  });

  export default {
    name: 'OperationsSearch',
    components: {
      AutocompleteAnalysis,
    },
    data() {
      return {
        formData: { ...initialFormData },
      };
    },
    computed: {
      ...mapGetters(['forms']),
    },
    methods: {
      ...mapActions(['updateForm']),
      async searchAnalyses({ text, cb, field }) {
        if (text.length >= 3) {
          const response = await analysesApi.autocompleteFetch(
            field,
            text,
            true,
            false
          );
          cb(response.data.map(mapAnalysisSuggestion));
        } else {
          cb([]);
        }
      },
      async searchAnalysesById({ text, cb }) {
        if (!text || !Number.isInteger(Number(text))) {
          cb([]);
          return;
        }
        try {
          const response = await analysesApi.autocompleteFetchById(text, false);
          cb(response.data.map(mapAnalysisSuggestion));
        } catch {
          cb([]);
        }
      },
      searchOperations(item) {
        this.formData.code = item.code;
        this.formData.analysisId = item.value;
        this.formData.analysisNumber = item.value;
        this.formData.description = item.description;
        this.formData.instrument = item.instrument;
        this.formData.method = item.method;
        this.formData.methodCode = item.methodCode;
        this.formData.showTemperature = item.showTemperature;
        this.formData.showSymbol = item.showSymbol;
        this.formData.showDilution = item.showDilution;
        this.formData.defaultDilution = item.defaultDilution;
        this.updateForm({ formKey: 'operations', formData: this.formData });
        this.$emit('handle-operations-list-update');
      },
      emptySelectedAnalysis(text) {
        if (text === '') {
          this.formData.analysisId = null;
          this.formData.analysisNumber = null;
          this.formData.code = null;
          this.formData.description = null;
          this.formData.instrument = null;
          this.formData.method = null;
          this.formData.methodCode = null;
          this.updateForm({ formKey: 'operations', formData: this.formData });
          this.$emit('handle-operations-list-update');
        }
      },
    },
    watch: {
      'forms.operations': {
        handler(data) {
          this.formData = { ...initialFormData, ...data };
        },
        deep: true,
      },
    },
    created() {
      this.formData = { ...initialFormData, ...this.forms.operations };
    },
  };
</script>

<style lang="scss" scoped>
  .operations-search-form {
    padding-bottom: 20px;
    min-height: $filter-form-min-height;

    .el-form-item {
      &__label {
        color: $--color-text-regular;
        min-height: $filter-label-min-height;
      }

      &--small {
        .el-form-item {
          &__label {
            padding-bottom: 15px;
            line-height: 1;
            min-height: $filter-label-min-height;
          }
        }
      }

      .el-autocomplete {
        width: 100%;
      }
    }
  }
</style>
